import { Checkbox, Spin, Table } from 'antd';
import { ColumnProps } from 'antd/es/table';
import React, { useCallback, useMemo } from 'react';
import { useTypedSelector } from '../../../../core-module/redux';
import { absenceClient } from '../../../../core-module/services/absence/AbsenceClient';
import {
  DeployabilityDay,
  DeployabilityOption
} from '../../../../core-module/services/absence/types';
import { DayNote } from './DayNote';
import {
  findDeployabilityOption,
  findDriverNoteByDeployabilityDay,
  findDriverToDeployabilityOption,
  getAllUnselectedOptionsForADay,
  getDriverDeployabilityOptionsForADay,
  hasDriverSelectedAllOptionsForADay
} from './helpers';
import { useAllDeployabilityOptionsQuery } from './useAllDeployabilityOptionsQuery';
import { useDriverDeployabilityQuery } from './useDriverDeployabilityOptionsQuery';

interface DeployabilityOptionsByDay {
  deployabilityDay: DeployabilityDay;
  deployabilityOptions: DeployabilityOption[];
}

const DeployabilityOverviewContainer: React.FC = () => {
  const driverId = useTypedSelector((state) => state.auth.user.driver!.id);

  const allDeployabilityOptionsState = useAllDeployabilityOptionsQuery();
  const driverDeployabilityState = useDriverDeployabilityQuery(driverId);

  const allOptionsData = useMemo(() => {
    if (allDeployabilityOptionsState.type === 'USABLE') {
      return allDeployabilityOptionsState.data;
    }

    return undefined;
  }, [allDeployabilityOptionsState]);

  const driverDeployabilityData = useMemo(() => {
    if (
      driverDeployabilityState.type === 'USABLE' ||
      driverDeployabilityState.type === 'FETCHING'
    ) {
      return driverDeployabilityState.data;
    }

    return undefined;
  }, [driverDeployabilityState]);

  const refresh = useCallback(async () => {
    if (driverDeployabilityState.refetch) {
      driverDeployabilityState.refetch();
    }
  }, [driverDeployabilityState]);

  const createDriverDeployabilityOptions = useCallback(
    async (deployabilityOptionIds: number[]) => {
      const promisePool: Promise<boolean>[] = [];
      for (const deployabilityOptionId of deployabilityOptionIds) {
        const promise = absenceClient.createDriverDeployabilityOption(
          driverId,
          deployabilityOptionId
        );

        promisePool.push(promise);
      }

      Promise.all(promisePool)
        .then(() => {
          if (driverDeployabilityState.refetch) {
            driverDeployabilityState.refetch();
          }
        })
        .catch();
    },
    [driverId, driverDeployabilityState]
  );
  const removeDriverDeployabilityOptions = useCallback(
    async (driverDeployabilityOptionIds: number[]) => {
      const promisePool: Promise<void>[] = [];
      for (const driverDeployabilityOptionId of driverDeployabilityOptionIds) {
        const promise = absenceClient.removeDriverDeployabilityOption(
          driverId,
          driverDeployabilityOptionId
        );

        promisePool.push(promise);
      }

      Promise.all(promisePool)
        .then(() => {
          if (driverDeployabilityState.refetch) {
            driverDeployabilityState.refetch();
          }
        })
        .catch();
    },
    [driverId, driverDeployabilityState]
  );

  const columns: ColumnProps<DeployabilityOptionsByDay>[] = useMemo(() => {
    if (!allOptionsData || !driverDeployabilityData) {
      return [];
    }

    const columns: ColumnProps<DeployabilityOptionsByDay>[] = [
      {
        title: 'Dag',
        key: 'day',
        width: '15%',
        render: (text, record, index) => record.deployabilityDay?.name || ''
      },
      {
        title: 'Hele dag',
        key: 'all_day_moments_select',
        width: '15%',
        render: (_, record) => {
          const hasSelectedAllForDay = hasDriverSelectedAllOptionsForADay(
            driverDeployabilityData.driverDeployabilityOptions,
            record.deployabilityOptions
          );

          return (
            <div>
              <Checkbox
                checked={hasSelectedAllForDay}
                onChange={async (e) => {
                  if (e.target.checked) {
                    const unselectedOptionsForDay = getAllUnselectedOptionsForADay(
                      driverDeployabilityData.driverDeployabilityOptions,
                      record.deployabilityOptions
                    );

                    const ids = unselectedOptionsForDay.map((x) => x.id);
                    await createDriverDeployabilityOptions(ids);
                  } else {
                    const driverDeployabilityOptionsForDay = getDriverDeployabilityOptionsForADay(
                      driverDeployabilityData.driverDeployabilityOptions,
                      record.deployabilityOptions
                    );

                    const ids = driverDeployabilityOptionsForDay.map((x) => x.id);
                    await removeDriverDeployabilityOptions(ids);
                  }
                }}
              />
            </div>
          );
        }
      }
    ];

    for (const dayMoment of allOptionsData.deployabilityDayMoments) {
      const column = {
        title: dayMoment.name,
        key: dayMoment.name,
        width: '15%',
        render: (_, record: DeployabilityOptionsByDay) => {
          const foundOption = findDeployabilityOption(
            allOptionsData.deployabilityOptions,
            record.deployabilityDay.id,
            dayMoment.id
          );

          if (foundOption) {
            const foundDriverToDeployabilityOption = findDriverToDeployabilityOption(
              driverDeployabilityData.driverDeployabilityOptions,
              foundOption
            );

            return (
              <div>
                <Checkbox
                  checked={foundDriverToDeployabilityOption !== undefined}
                  onChange={(e) => {
                    if (e.target.checked) {
                      createDriverDeployabilityOptions([foundOption.id]);
                    } else {
                      if (foundDriverToDeployabilityOption) {
                        removeDriverDeployabilityOptions([foundDriverToDeployabilityOption.id]);
                      }
                    }
                  }}
                />
              </div>
            );
          }

          return null;
        }
      };
      columns.push(column);
    }

    columns.push({
      title: 'Notitie',
      key: 'day_note',
      render: (_, record) => {
        const foundNote = findDriverNoteByDeployabilityDay(
          driverDeployabilityData.driverDeployabilityDayNotes,
          record.deployabilityDay
        );

        const note = foundNote?.note || undefined;

        return (
          <DayNote
            deployabilityDayId={record.deployabilityDay.id}
            driverDeployabilityDayNoteId={foundNote?.id}
            note={note}
            refresh={refresh}
          />
        );
      }
    });

    return columns;
  }, [
    allOptionsData,
    createDriverDeployabilityOptions,
    driverDeployabilityData,
    refresh,
    removeDriverDeployabilityOptions
  ]);

  const deployabilityOptionsByDay: DeployabilityOptionsByDay[] = useMemo(() => {
    if (!allOptionsData) {
      return [];
    }

    const list: DeployabilityOptionsByDay[] = allOptionsData.deployabilityDays.map((day) => ({
      deployabilityDay: day,
      deployabilityOptions: allOptionsData.deployabilityOptions.filter(
        (option) => option.deployabilityDayId === day.id
      )
    }));

    return list;
  }, [allOptionsData]);

  const loading =
    allDeployabilityOptionsState.type === 'LOADING' || driverDeployabilityState.type === 'LOADING';

  return (
    <Spin spinning={loading} delay={500}>
      <Table
        rowKey={(row, index) => index.toString()}
        dataSource={deployabilityOptionsByDay}
        locale={{ emptyText: 'Geen opties gevonden' }}
        bordered={true}
        pagination={false}
        columns={columns}
        loading={loading}
      />
    </Spin>
  );
};

export { DeployabilityOverviewContainer };
